import * as React from 'react';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { dollarFormatter } from 'af-utils/format.util';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import { Button } from '@acceligentllc/storybook';

import BillingTypeIndicator from '../../BillingTypeIndicator';
import { toCSVData } from '../../helpers';
import BillableAndNonBillableWorkTable from '../Table/BillableAndNonBillableWorkTable';

interface Props {
	workSummary: WorkSummaryVM[];
	billingCodes: Record<string, W_BillingCode_FindForJob_VM_Single[]>;
	workSummaryDetails: WorkSummaryDetailVM[];
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailVM[]>;
	areFRsReadOnly: boolean;
	isWorkSummaryStatusCompleted: boolean;
	isWorkSummaryStatusReviewed: boolean;
	alternativeWorkSummary: Set<WorkSummaryVM>;
	workOrderCode: string;
	belongsToProject: boolean;
	subJobs: SiblingSubjobVM[];
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	onReallocateAll: (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => void;
	fieldReportId: number;
	workOrderId: number;
	onBulkEditDetailBillableStatus: (workSummaryDetailIds: number[], isBillable: boolean) => void;
}

const NonBillableWork: React.FC<Props> = (props) => {
	const {
		workSummary,
		billingCodes,
		workSummaryDetails,
		onRowHighlight,
		highlightedGroup,
		isWorkSummaryStatusCompleted,
		areFRsReadOnly,
		isWorkSummaryStatusReviewed,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		alternativeWorkSummary,
		workOrderCode,
		belongsToProject,
		handleJobReallocation,
		subJobs,
		onReallocateAll,
		fieldReportId,
		workOrderId,
		onBulkEditDetailBillableStatus,
	} = props;

	const totalNonBillableWork = workSummary.reduce((_acc, _curr) => {
		return _acc + (_curr.total ?? 0);
	}, 0);
	const showHint = totalNonBillableWork === 0;

	const headerClassName = bemElement('field-report__work-summary__billable-work', 'header');
	const nonBillableWorkClassName = bemBlock('field-report__work-summary__billable-work', { 'non-billable': true });

	const onExportClick = React.useCallback(() => {
		const csvData = toCSVData(workSummary, belongsToProject, WorkSummaryBillingType.BILLABLE_WORK);
		downloadCSV(csvData, `${workOrderCode}_non_billable_work.csv`);
	}, [belongsToProject, workOrderCode, workSummary]);

	const handleBulkEditDetailBillableStatus = React.useCallback((workSummaryDetailIds: number[]) => {
		onBulkEditDetailBillableStatus(workSummaryDetailIds, true);
	}, [onBulkEditDetailBillableStatus]);

	const isWorkSummaryReadonly = isWorkSummaryStatusCompleted || areFRsReadOnly || isWorkSummaryStatusReviewed;
	const isBillableStatusEditable = !(isWorkSummaryStatusCompleted || areFRsReadOnly);

	return (
		<div className={nonBillableWorkClassName}>
			<div className={headerClassName}>
				<BillingTypeIndicator billingType={WorkSummaryBillingType.BILLABLE_WORK} />
				<span className={bemElement(headerClassName, 'title')}>
					Non-Billable Work
				</span>
				<Button
					disabled={!workSummary.length}
					icon="ActionDownload"
					label="Export"
					onClick={onExportClick}
					style="link"
				/>
			</div>
			<BillableAndNonBillableWorkTable
				alternativeWorkSummary={alternativeWorkSummary}
				belongsToProject={belongsToProject}
				billingCodes={billingCodes}
				billingType={WorkSummaryBillingType.BILLABLE_WORK}
				fieldReportId={fieldReportId}
				handleJobReallocation={handleJobReallocation}
				highlightedGroup={highlightedGroup}
				highlightedGroupKey={highlightedGroupKey}
				isBillable={false}
				isBillableStatusEditable={isBillableStatusEditable}
				isReadOnly={isWorkSummaryReadonly}
				onBulkEditDetailBillableStatus={handleBulkEditDetailBillableStatus}
				onReallocateAll={onReallocateAll}
				onRowHighlight={onRowHighlight}
				subJobs={subJobs}
				workOrderId={workOrderId}
				workSummary={workSummary}
				workSummaryDetails={workSummaryDetails}
				workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
			/>
			<div className={bemElement('field-report__work-summary__billable-work', 'total')}>
				{showHint && (
					<span className={bemElement('field-report__work-summary__billable-work__total', 'hint')}>
						Non-billable total is calculated after work with a billing code is moved to non-billable table
					</span>
				)}
				{`Non-billable total ${dollarFormatter.format(totalNonBillableWork)}`}
			</div>
		</div>
	);
};

export default React.memo(NonBillableWork);
